// ============================================================================
// Shared widget shell — one rounded card with a title + optional sub-line and
// a children slot. Every dashboard widget renders inside this so the grid
// stays visually consistent without each widget re-implementing the chrome.
//
// Style intent: uses the shared Card primitive so widgets and facility cards
// feel like the same family. Title row uses the .meta uppercase eyebrow
// pattern from tcs.css.
// ============================================================================

import type { ReactNode } from 'react';
import { Card, MetaLabel, Skeleton } from '../../shared/components';

interface WidgetCardProps {
  title: string;
  subtitle?: string;
  // Right-side accessory rendered next to the title — e.g. a "View all" link
  // or a small filter pill. Optional; falls back to nothing.
  accessory?: ReactNode;
  // Optional fixed min-height in px so widgets in the same grid row align even
  // when their content has different natural heights (e.g. chart vs list).
  minHeight?: number;
  children: ReactNode;
}

export function WidgetCard({
  title,
  subtitle,
  accessory,
  minHeight,
  children,
}: WidgetCardProps) {
  return (
    <Card
      as="section"
      className="flex flex-col p-5"
    >
      <div
        className="flex flex-col flex-1"
        style={minHeight ? { minHeight } : undefined}
      >
        <header className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <MetaLabel>{title}</MetaLabel>
            {subtitle && (
              <p className="mt-0.5 text-xs text-tcs-text-muted">{subtitle}</p>
            )}
          </div>
          {accessory && <div className="shrink-0">{accessory}</div>}
        </header>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </Card>
  );
}

// Skeleton placeholder used by widgets while their data is loading on first
// paint. Subsequent refreshes keep the prior values up so the dashboard
// doesn't blink — this skeleton only fires for the initial empty state.
// Uses the shared Skeleton component (proper shimmer keyframe, not pulse).
export function WidgetSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-2" aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} block height={12} width="100%" />
      ))}
    </div>
  );
}

// Compact USD formatter shared by widgets. Numbers under 10k render with a
// dollar sign and no decimals; larger numbers shorten to $X.Xk / $X.XM so
// KPI cards stay readable at small widths.
export function fmtCompactCurrency(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `$${(n / 1_000).toFixed(1)}k`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(2)}k`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

export function fmtPct(n: number, digits = 1): string {
  if (!Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}%`;
}
